import { withFocusRing } from "@components/Accessibility/FocusRing/FocusRing";
import FormCheckbox from "@components/Form/FormCheckbox";
import { styledWithInvalidProps } from "@components/Styled/StyledWithInvalidProps";
import { css } from "@emotion/react";
import React from "react";
import tw from "twin.macro";
import {
  FilterSectionSwatchOutputProps,
  withFilterSectionSwatch,
} from "./withFilterSectionSwatch";

type SwatchProps = {
  active: boolean;
  colour: string;
  image: string | false;
};

const List = tw.ul`
  block pt-maxi pb-micro list-none grid grid-cols-2 lg:grid-cols-1 xl:grid-cols-2
`;

const ListItem = styledWithInvalidProps("li")(({ show }: { show: boolean }) => [
  tw`ml-pico`,
  show ? tw`block` : tw`hidden`,
]);

const FilterIcon = withFocusRing(
  styledWithInvalidProps("button")(({ colour, image, active }: SwatchProps) => [
    tw`relative flex mb-mini bg-cover border border-current rounded-full transition-layout w-maxi h-maxi mr-mini md:h-midi md:mr-mini md:mb-mini md:w-midi focus:outline-none`,
    tw`after:absolute after:rounded-full after:border after:border-solid after:border-current after:opacity-0 after:-top-pico after:-right-pico after:-bottom-pico after:-left-pico`,
    active ? tw`after:opacity-100` : tw`after:group-hover:opacity-100`,
    colour &&
      css`
        background-color: ${colour};
      `,
    image &&
      css`
        background-image: url(${image});
        border-color: transparent;
      `,
  ]),
);
const FilterLabel = styledWithInvalidProps("label")(
  ({ active }: { active: boolean }) => [
    tw`pl-mega md:pl-maxi align-middle capitalize`,
    active ? tw`underline` : "",
  ],
);

const Wrapper = tw.div`
  flex w-1/2 mb-mini items-center justify-start transition-presentation hover:text-orange 
`;

const Button = withFocusRing(tw.button`
   pb-nano border-b text-primary
`);

const Checkbox = withFocusRing(tw(FormCheckbox)`
  absolute inset-0 pr-peta w-full h-full appearance-none opacity-0 cursor-pointer z-100 focus-visible:opacity-100
`);

const FilterProductCount = tw.div`ml-pico text-primary`;

const EmptyFilterLabel = tw.p`
  text-quaternary
`;

const Container = tw.div`
  flex items-center
`;

export const FilterSectionSwatch = withFilterSectionSwatch(
  ({
    options,
    value,
    handleChange,
    showMore,
    setShowMore,
    handleScroll,
  }: FilterSectionSwatchOutputProps) => {
    return (
      <>
        <List>
          {!options.length ? (
            <EmptyFilterLabel>No products found</EmptyFilterLabel>
          ) : null}
          {options?.map(({ key, swatch, doc_count }, index) => {
            return (
              <ListItem
                key={key.toString()}
                show={index < 15 ? true : showMore}
                onClick={handleScroll}
              >
                <FilterIcon
                  active={value[key] || value === key}
                  title={key}
                  colour={swatch?.colour}
                  image={swatch?.image}
                >
                  <Container>
                    <Checkbox
                      id={`checkbox_${key}`}
                      value={key}
                      checked={value[key]}
                      onClick={handleChange}
                      handleChange={handleChange}
                      onKeyPress={handleChange}
                      internalState
                      tabIndex={-1}
                    />
                    <FilterLabel
                      active={value[key]}
                      data-testid="filter-swatch"
                      htmlFor={`checkbox_${key}`}
                    >
                      {key}
                    </FilterLabel>
                    <FilterProductCount>{`(${doc_count})`}</FilterProductCount>
                  </Container>
                </FilterIcon>
              </ListItem>
            );
          })}
        </List>
        <Wrapper>
          {options.length > 15 ? (
            <Button onClick={() => setShowMore(!showMore)}>
              <span>{showMore ? "See less -" : "See more +"}</span>
            </Button>
          ) : null}
        </Wrapper>
      </>
    );
  },
);
